import React from "react";


function clean(
  value
) {
  return value == null
    ? ""
    : String(value).trim();
}


function countWords(
  value
) {
  const text =
    clean(value);

  return text
    ? text.split(/\s+/).length
    : 0;
}


function unitTypeLabel(
  value,
  isFR
) {
  const labels = {
    single_page:
      isFR
        ? "Page simple"
        : "Single page",

    double_page_spread:
      isFR
        ? "Double page"
        : "Two-page spread",

    front_matter:
      isFR
        ? "Page liminaire"
        : "Front matter page",

    back_matter:
      isFR
        ? "Page finale"
        : "Back matter page",

    activity_page:
      isFR
        ? "Page d’activité"
        : "Activity page",
  };

  return (
    labels[
      clean(value)
    ] ||
    (
      isFR
        ? "Page ou double page"
        : "Page or spread"
    )
  );
}


function targetLabel(
  unit,
  isFR
) {
  if (!unit) {
    return "";
  }

  const title =
    clean(
      unit?.workingTitle
    ) ||
    unitTypeLabel(
      unit?.unitType,
      isFR
    );

  const position =
    clean(
      unit?.pageNumber
    );

  return position
    ? `${title} — ${
        isFR
          ? "Page ou repère"
          : "Page or position"
      } ${position}`
    : title;
}


export const MANUSCRIPT_IMPORT_DECISIONS = {
  ADD:
    "add",

  APPEND:
    "append",

  REPLACE:
    "replace",

  SKIP:
    "skip",
};


export function buildManuscriptImportDecisionRows(
  sections = [],
  mappingBySection = {},
  bookMapUnits = [],
  manuscriptEntries = [],
  lang = "en"
) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const safeSections =
    Array.isArray(sections)
      ? sections
      : [];

  const safeUnits =
    Array.isArray(bookMapUnits)
      ? bookMapUnits
      : [];

  const safeEntries =
    Array.isArray(
      manuscriptEntries
    )
      ? manuscriptEntries
      : [];

  return safeSections.map(
    (
      section,
      index
    ) => {
      const sectionId =
        clean(
          section?.id
        ) ||
        `section-${index + 1}`;

      const unitId =
        clean(
          mappingBySection?.[
            sectionId
          ]
        );

      const unit =
        safeUnits.find(
          (candidate) =>
            clean(
              candidate?.id
            ) === unitId
        ) ||
        null;

      const entry =
        unit
          ? safeEntries.find(
              (candidate) =>
                clean(
                  candidate?.unitId
                ) === unitId
            ) ||
            null
          : null;

      const suppliedWords =
        Number(
          section?.wordCount
        );

      return {
        sectionId,

        sectionNumber:
          index + 1,

        title:
          clean(
            section?.title
          ) ||
          `Section ${index + 1}`,

        importedWordCount:
          Number.isFinite(
            suppliedWords
          ) &&
          suppliedWords >= 0
            ? suppliedWords
            : countWords(
                section?.text
              ),

        unitId:
          unit
            ? clean(
                unit.id
              )
            : "",

        targetLabel:
          targetLabel(
            unit,
            isFR
          ),

        placed:
          Boolean(unit),

        existingWordCount:
          countWords(
            entry?.manuscriptText
          ),
      };
    }
  );
}


export function allowedManuscriptImportDecisions(
  row
) {
  if (!row?.placed) {
    return [
      MANUSCRIPT_IMPORT_DECISIONS
        .SKIP,
    ];
  }

  if (
    Number(
      row.existingWordCount ||
        0
    ) > 0
  ) {
    return [
      MANUSCRIPT_IMPORT_DECISIONS
        .SKIP,

      MANUSCRIPT_IMPORT_DECISIONS
        .APPEND,

      MANUSCRIPT_IMPORT_DECISIONS
        .REPLACE,
    ];
  }

  return [
    MANUSCRIPT_IMPORT_DECISIONS
      .SKIP,

    MANUSCRIPT_IMPORT_DECISIONS
      .ADD,
  ];
}


export function nextManuscriptImportDecision(
  current = {},
  sectionId,
  decision
) {
  const safeCurrent =
    current &&
    typeof current ===
      "object" &&
    !Array.isArray(current)
      ? current
      : {};

  const id =
    clean(sectionId);

  if (!id) {
    return {
      ...safeCurrent,
    };
  }

  return {
    ...safeCurrent,
    [id]:
      clean(decision),
  };
}


export function manuscriptImportDecisionSummary(
  rows = [],
  decisionBySection = {}
) {
  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];

  let decided = 0;
  let replacements = 0;
  let skipped = 0;

  safeRows.forEach(
    (row) => {
      const decision =
        clean(
          decisionBySection?.[
            row.sectionId
          ]
        );

      const allowed =
        allowedManuscriptImportDecisions(
          row
        );

      if (
        !decision ||
        !allowed.includes(
          decision
        )
      ) {
        return;
      }

      decided += 1;

      if (
        decision ===
        MANUSCRIPT_IMPORT_DECISIONS
          .REPLACE
      ) {
        replacements += 1;
      }

      if (
        decision ===
        MANUSCRIPT_IMPORT_DECISIONS
          .SKIP
      ) {
        skipped += 1;
      }
    }
  );

  return {
    total:
      safeRows.length,

    decided,

    undecided:
      Math.max(
        0,
        safeRows.length -
          decided
      ),

    replacements,

    skipped,
  };
}


export default function ChildrensBookManuscriptImportDecision({
  lang = "en",
  sections = [],
  mappingBySection = {},
  bookMapUnits = [],
  manuscriptEntries = [],
  decisionBySection = {},
  onDecisionChange,
}) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const rows =
    buildManuscriptImportDecisionRows(
      sections,
      mappingBySection,
      bookMapUnits,
      manuscriptEntries,
      lang
    );

  const summary =
    manuscriptImportDecisionSummary(
      rows,
      decisionBySection
    );

  const ui = {
    title: isFR
      ? "Choisir ce qui doit arriver à chaque section"
      : "Choose What Should Happen to Each Section",

    badge: isFR
      ? "Décisions uniquement"
      : "Decisions only",

    body: isFR
      ? "Choisissez une instruction pour chaque section Word. Ces choix sont temporaires : aucun texte ne sera encore ajouté, remplacé ou enregistré."
      : "Choose an instruction for each Word section. These choices are temporary: no text will be added, replaced, or saved yet.",

    decided: isFR
      ? "Décisions prises"
      : "Decisions made",

    undecided: isFR
      ? "Encore à décider"
      : "Still to decide",

    replacements: isFR
      ? "Remplacements à confirmer"
      : "Replacement confirmations",

    skipped: isFR
      ? "Sections ignorées"
      : "Sections skipped",

    wordSection: isFR
      ? "Section Word"
      : "Word section",

    destination: isFR
      ? "Destination"
      : "Destination",

    noDestination: isFR
      ? "Aucune destination choisie"
      : "No destination chosen",

    importedWords: isFR
      ? "Mots Word"
      : "Word document words",

    existingWords: isFR
      ? "Mots existants"
      : "Existing manuscript words",

    question: isFR
      ? "Que souhaitez-vous faire de cette section ?"
      : "What should happen to this section?",

    noDestinationHelp: isFR
      ? "Cette section n’a pas de destination. Vous pouvez l’ignorer ici ou revenir aux choix de destination."
      : "This section has no destination. You may skip it here or return to placement choices.",

    replacementWarning: isFR
      ? "Le remplacement du texte existant est destructif. Une confirmation finale distincte sera exigée avant toute modification."
      : "Replacing existing manuscript text is destructive. A separate final confirmation will be required before it can happen.",

    unchanged: isFR
      ? "Ces décisions ne modifient ni le manuscrit ni le Blueprint. Elles restent uniquement dans cet onglet du navigateur."
      : "These decisions do not change the manuscript or Blueprint. They remain only in this browser tab.",

    optionLabel(
      decision
    ) {
      const labels = {
        add: isFR
          ? "Ajouter cette section Word à la destination vide"
          : "Add this Word section to the empty destination",

        append: isFR
          ? "Ajouter cette section Word après le texte existant"
          : "Add this Word section after the existing manuscript text",

        replace: isFR
          ? "Remplacer le texte existant par cette section Word"
          : "Replace the existing manuscript text with this Word section",

        skip: isFR
          ? "Ne pas ajouter cette section Word"
          : "Do not add this Word section",
      };

      return (
        labels[decision] ||
        decision
      );
    },
  };

  function updateDecision(
    sectionId,
    decision
  ) {
    if (
      typeof onDecisionChange !==
      "function"
    ) {
      return;
    }

    onDecisionChange(
      nextManuscriptImportDecision(
        decisionBySection,
        sectionId,
        decision
      )
    );
  }

  return (
    <section
      aria-labelledby="manuscript-import-decision-title"
      style={{
        padding: 18,
        borderRadius: 16,
        border:
          "2px solid #7c3aed",
        background:
          "linear-gradient(145deg, #ffffff 0%, #f5f3ff 100%)",
        boxShadow:
          "0 10px 24px rgba(124,58,237,0.09)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems:
            "flex-start",
          justifyContent:
            "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 780,
          }}
        >
          <h4
            id="manuscript-import-decision-title"
            style={{
              margin: 0,
              fontSize: 21,
              lineHeight: 1.3,
            }}
          >
            {ui.title}
          </h4>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "8px 0 0",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            {ui.body}
          </p>
        </div>

        <span
          style={{
            display:
              "inline-flex",
            padding:
              "6px 10px",
            borderRadius: 999,
            border:
              "1px solid #c4b5fd",
            background:
              "#ede9fe",
            color:
              "#5b21b6",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {ui.badge}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(145px, 1fr))",
          gap: 10,
          marginTop: 16,
        }}
      >
        {[
          [
            ui.decided,
            `${summary.decided} of ${summary.total}`,
          ],
          [
            ui.undecided,
            summary.undecided,
          ],
          [
            ui.replacements,
            summary.replacements,
          ],
          [
            ui.skipped,
            summary.skipped,
          ],
        ].map(
          ([
            label,
            value,
          ]) => (
            <div
              key={label}
              aria-label={label}
              style={{
                padding: 13,
                borderRadius: 12,
                border:
                  "1px solid #ddd6fe",
                background:
                  "#ffffff",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color:
                    "#64748b",
                }}
              >
                {label}
              </div>

              <strong
                style={{
                  display:
                    "block",
                  marginTop: 5,
                  fontSize: 20,
                }}
              >
                {value}
              </strong>
            </div>
          )
        )}
      </div>

      <div
        style={{
          display: "grid",
          gap: 15,
          marginTop: 18,
        }}
      >
        {rows.map(
          (row) => {
            const selected =
              clean(
                decisionBySection?.[
                  row.sectionId
                ]
              );

            const allowed =
              allowedManuscriptImportDecisions(
                row
              );

            const replacing =
              selected ===
              MANUSCRIPT_IMPORT_DECISIONS
                .REPLACE;

            return (
              <article
                key={
                  row.sectionId
                }
                style={{
                  padding: 16,
                  borderRadius: 15,
                  border:
                    selected
                      ? "2px solid #8b5cf6"
                      : "2px solid #c4b5fd",
                  background:
                    selected
                      ? "#faf5ff"
                      : "#ffffff",
                }}
              >
                <div
                  style={{
                    display:
                      "flex",
                    alignItems:
                      "flex-start",
                    gap: 11,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display:
                        "inline-grid",
                      placeItems:
                        "center",
                      flex:
                        "0 0 auto",
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background:
                        "#7c3aed",
                      color:
                        "#ffffff",
                      fontWeight: 900,
                    }}
                  >
                    {
                      row.sectionNumber
                    }
                  </span>

                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing:
                          "0.1em",
                        textTransform:
                          "uppercase",
                        color:
                          "#64748b",
                      }}
                    >
                      {ui.wordSection}
                    </div>

                    <h5
                      style={{
                        margin:
                          "3px 0 0",
                        fontSize: 18,
                        lineHeight: 1.3,
                      }}
                    >
                      {row.title}
                    </h5>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(170px, 1fr))",
                    gap: 10,
                    marginTop: 14,
                  }}
                >
                  {[
                    [
                      ui.destination,
                      row.targetLabel ||
                        ui.noDestination,
                    ],
                    [
                      ui.importedWords,
                      row.importedWordCount
                        .toLocaleString(),
                    ],
                    [
                      ui.existingWords,
                      row.existingWordCount
                        .toLocaleString(),
                    ],
                  ].map(
                    ([
                      label,
                      value,
                    ]) => (
                      <div
                        key={
                          label
                        }
                        style={{
                          padding:
                            "10px 12px",
                          borderRadius: 11,
                          border:
                            "1px solid #ddd6fe",
                          background:
                            "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color:
                              "#64748b",
                          }}
                        >
                          {label}
                        </div>

                        <div
                          style={{
                            marginTop: 5,
                            fontWeight: 700,
                            lineHeight: 1.45,
                          }}
                        >
                          {value}
                        </div>
                      </div>
                    )
                  )}
                </div>

                {!row.placed ? (
                  <p
                    style={{
                      margin:
                        "12px 0 0",
                      padding:
                        "10px 12px",
                      borderRadius: 11,
                      border:
                        "1px solid #fdba74",
                      background:
                        "#fff7ed",
                      color:
                        "#9a3412",
                      lineHeight: 1.55,
                      fontWeight: 600,
                    }}
                  >
                    {
                      ui.noDestinationHelp
                    }
                  </p>
                ) : null}

                <fieldset
                  style={{
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 12,
                    border:
                      "1px solid #c4b5fd",
                    background:
                      "#ffffff",
                  }}
                >
                  <legend
                    style={{
                      padding:
                        "0 6px",
                      fontWeight: 800,
                      color:
                        "#312e81",
                    }}
                  >
                    {ui.question}
                  </legend>

                  <div
                    style={{
                      display: "grid",
                      gap: 9,
                    }}
                  >
                    {allowed.map(
                      (
                        decision
                      ) => (
                        <label
                          key={
                            decision
                          }
                          style={{
                            display:
                              "flex",
                            alignItems:
                              "flex-start",
                            gap: 9,
                            padding:
                              "10px 11px",
                            borderRadius: 10,
                            border:
                              selected ===
                              decision
                                ? "2px solid #8b5cf6"
                                : "1px solid #ddd6fe",
                            background:
                              selected ===
                              decision
                                ? "#f5f3ff"
                                : "#ffffff",
                            cursor:
                              "pointer",
                            fontWeight: 600,
                            lineHeight: 1.45,
                          }}
                        >
                          <input
                            type="radio"
                            name={
                              `manuscript-import-decision-${row.sectionId}`
                            }
                            value={
                              decision
                            }
                            checked={
                              selected ===
                              decision
                            }
                            onChange={
                              () =>
                                updateDecision(
                                  row.sectionId,
                                  decision
                                )
                            }
                          />

                          <span>
                            {
                              ui.optionLabel(
                                decision
                              )
                            }
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </fieldset>

                {replacing ? (
                  <div
                    role="alert"
                    style={{
                      marginTop: 12,
                      padding:
                        "10px 12px",
                      borderRadius: 11,
                      border:
                        "1px solid #fca5a5",
                      background:
                        "#fef2f2",
                      color:
                        "#991b1b",
                      lineHeight: 1.55,
                      fontWeight: 700,
                    }}
                  >
                    {
                      ui.replacementWarning
                    }
                  </div>
                ) : null}
              </article>
            );
          }
        )}
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin:
            "16px 0 0",
          lineHeight: 1.6,
          fontWeight: 500,
        }}
      >
        {ui.unchanged}
      </p>
    </section>
  );
}
